import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { menuItems } from '../../config/sidebar.config';
import { cn } from '../../utils/utils';
import { PanelLeft, PanelLeftClose } from 'lucide-react';

// Importer uniquement les composants UI utilisés
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

interface SidebarProps {
  children: React.ReactNode;
}

export const Sidebar: React.FC<SidebarProps> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  // Fonction utilitaire pour vérifier si l'utilisateur a un rôle particulier
  const hasRole = (role?: string): boolean => {
    if (!role) return true; // Si pas de rôle requis, retourner true
    if (!currentUser) return false; // Si pas d'utilisateur connecté, retourner false pour les rôles requis
    return currentUser.role === role;
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Filtrer les items visibles et les grouper par catégorie
  const visibleItems = menuItems.filter(item => {
    const shouldShow = !item.requiredRole || hasRole(item.requiredRole);
    return shouldShow;
  });

  // Séparer l'item Settings des autres
  const settingsItem = visibleItems.find(item => item.path === '/app/admin-settings');
  const regularItems = visibleItems.filter(item => item.path !== '/app/admin-settings');

  // Grouper les items réguliers par catégorie
  const groupedItems = regularItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, typeof regularItems>);

  // Ordre des catégories
  const categoryOrder = ['adblock', 'ssp', 'noconsent', 'admin'];
  const orderedCategories = categoryOrder.filter(cat => groupedItems[cat] && groupedItems[cat].length > 0);

  return (
    <div className="flex h-full">
      {/* Sidebar principale fixe avec classes dynamiques */}
      <div 
        className={cn(
          "fixed left-0 top-0 h-full bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800 z-40 transition-all duration-300",
          isCollapsed ? "w-[70px]" : "w-[240px]"
        )}
      >
        <div className="space-y-4 py-4 pt-16 flex-1 h-screen flex flex-col overflow-hidden">
          {/* Section principale avec scroll */}
          <div className="flex-1 overflow-y-auto px-3 py-2">
            <div className="space-y-6">
              {orderedCategories.map((category, categoryIndex) => (
                <div key={category}>
                  {/* Titre de la section (seulement en mode étendu) */}
                  {!isCollapsed && (
                    <div className="px-3 py-2">
                      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t(`sidebar.category.${category}`)}
                      </h3>
                    </div>
                  )}
                  
                  {/* Items de la catégorie */}
                  <div className="space-y-1">
                    {groupedItems[category].map((item) => {
                      const isActive = location.pathname === item.path;
                      const IconComponent = item.icon;
                      
                      return (
                        <div key={item.path} className="debug-item" data-path={item.path}>
                          <Tooltip delayDuration={isCollapsed ? 0 : 1000}>
                            <TooltipTrigger asChild>
                              <div>
                                <Button
                                  variant="ghost"
                                  size="lg"
                                  className={cn(
                                    'w-full flex justify-start px-3 py-2 h-10',
                                    isActive 
                                      ? 'bg-gray-100 dark:bg-gray-800 text-primary dark:text-primary' 
                                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900',
                                    isCollapsed && 'justify-center px-0'
                                  )}
                                  onClick={() => navigate(item.path)}
                                >
                                  <IconComponent className={cn('h-5 w-5', !isCollapsed && 'mr-3')} />
                                  {!isCollapsed && <span>{t(item.labelKey)}</span>}
                                </Button>
                              </div>
                            </TooltipTrigger>
                            {isCollapsed && (
                              <TooltipContent side="right" className="bg-gray-800 text-white border-gray-700">
                                {t(item.labelKey)}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Séparateur entre les catégories (sauf pour la dernière) */}
                  {categoryIndex < orderedCategories.length - 1 && (
                    <div className={cn(
                      "border-t border-gray-200 dark:border-gray-700",
                      isCollapsed ? "mx-2 my-3" : "mx-3"
                    )}></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Section Settings fixée en bas */}
          {settingsItem && (
            <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2">
              <div className="space-y-1">
                <Tooltip delayDuration={isCollapsed ? 0 : 1000}>
                  <TooltipTrigger asChild>
                    <div>
                      <Button
                        variant="ghost"
                        size="lg"
                        className={cn(
                          'w-full flex justify-start px-3 py-2 h-10',
                          location.pathname === settingsItem.path
                            ? 'bg-gray-100 dark:bg-gray-800 text-primary dark:text-primary' 
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900',
                          isCollapsed && 'justify-center px-0'
                        )}
                        onClick={() => navigate(settingsItem.path)}
                      >
                        <settingsItem.icon className={cn('h-5 w-5', !isCollapsed && 'mr-3')} />
                        {!isCollapsed && <span>{t(settingsItem.labelKey)}</span>}
                      </Button>
                    </div>
                  </TooltipTrigger>
                  {isCollapsed && (
                    <TooltipContent side="right" className="bg-gray-800 text-white border-gray-700">
                      {t(settingsItem.labelKey)}
                    </TooltipContent>
                  )}
                </Tooltip>
              </div>
            </div>
          )}
        </div>

        {/* Bouton toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-[-12px] top-20 h-8 w-8 rounded-full bg-white dark:bg-gray-900 shadow-md border border-gray-200 dark:border-gray-800 z-50"
          onClick={toggleSidebar}
          title={t('sidebar.toggleCollapse')}
          aria-label={t('sidebar.toggleCollapse')}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? (
            <PanelLeft className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </Button>
      </div>
      
      {/* Main content avec marge calculée pour correspondre à la largeur de la sidebar */}
      <div 
        className={cn(
          "flex-1 overflow-auto bg-gray-50 dark:bg-gray-900 min-h-screen transition-all duration-300",
          isCollapsed ? "ml-[70px]" : "ml-[240px]"
        )}
      >
        <div className="mt-16">{children}</div>
      </div>
    </div>
  );
}
